import { useMemo } from 'react';
import type Highcharts from 'highcharts';
import type { DirectShearReading } from '../../../../types/directShearReading';
import type { AnalysisData } from '../analysisTypes';
import type { MarkerRow } from '../chartConfig';
import { baseChartOptions, CHART_COLORS, buildColorZones, buildMarkerSeries } from '../chartConfig';

interface StressChartProps {
  readings: DirectShearReading[];
  analysis: AnalysisData;
  peakRow: MarkerRow;
  residualRow: MarkerRow;
}

export function useStressChartOptions({ readings, analysis, peakRow, residualRow }: StressChartProps): Highcharts.Options {
  return useMemo((): Highcharts.Options => {
    const shearData = readings
      .filter((r) => r.HorizontalDisplacementCumulative != null && r.CorrectedShearStress != null)
      .map((r) => [r.HorizontalDisplacementCumulative!, r.CorrectedShearStress!]);

    const uncorrectedData = readings
      .filter((r) => r.HorizontalDisplacementCumulative != null && r.UncorrectedShearStress != null)
      .map((r) => [r.HorizontalDisplacementCumulative!, r.UncorrectedShearStress!]);

    const zones = buildColorZones(analysis);
    const markers = buildMarkerSeries(peakRow, residualRow, 'CorrectedShearStress');

    return {
      ...baseChartOptions,
      title: { text: 'Corrected Shear Stress vs. Horizontal Displacement' },
      yAxis: {
        ...(baseChartOptions.yAxis as Highcharts.YAxisOptions),
        title: { text: 'Shear Stress (kPa)' },
      },
      series: [
        {
          type: 'spline',
          name: 'Uncorrected Shear Stress',
          data: uncorrectedData,
          color: CHART_COLORS.blue,
          dashStyle: 'Dash',
        },
        {
          type: 'spline',
          name: 'Corrected Shear Stress',
          data: shearData,
          color: CHART_COLORS.green,
          zoneAxis: zones ? 'x' : undefined,
          zones,
        },
        ...markers,
      ],
    };
  }, [readings, analysis, peakRow, residualRow]);
}
